import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { ICONS } from '@/constants/icons';
import { Toast as ToastType, ToastType as ToastVariant, useToastStore } from '@/stores/toast.store';

/**
 * Show a toast notification
 * Convenience function for triggering toasts
 *
 * @example
 * showToast('Profile updated!', 'success');
 * showToast('An error occurred', 'error');
 */
export const showToast = (message: string, type: ToastVariant = 'info', duration = 5000) => {
  const { addToast } = useToastStore.getState();
  addToast({ message, type, duration });
};

/**
 * Toast Component Props
 */
interface ToastProps {
  toast: ToastType;
  onClose: () => void;
}

/**
 * Toast variant configuration
 * Maps toast types to visual styles
 */
const TOAST_VARIANTS = {
  success: {
    icon: ICONS.CHECK_CIRCLE,
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-500',
    iconColor: 'text-green-600 dark:text-green-400',
    textColor: 'text-green-900 dark:text-green-100',
    focusRing: 'focus:ring-green-500',
  },
  error: {
    icon: ICONS.CROSS_CIRCLE,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-500',
    iconColor: 'text-red-600 dark:text-red-400',
    textColor: 'text-red-900 dark:text-red-100',
    focusRing: 'focus:ring-red-500',
  },
  warning: {
    icon: ICONS.WARNING,
    bgColor: 'bg-amber-50 dark:bg-amber-900/20',
    borderColor: 'border-amber-500',
    iconColor: 'text-amber-600 dark:text-amber-400',
    textColor: 'text-amber-900 dark:text-amber-100',
    focusRing: 'focus:ring-amber-500',
  },
  info: {
    icon: ICONS.INFO,
    bgColor: 'bg-soft-mint dark:bg-teal-900/20',
    borderColor: 'border-primary-500',
    iconColor: 'text-dark-teal dark:text-primary-400',
    textColor: 'text-dark-teal dark:text-primary-100',
    focusRing: 'focus:ring-primary-500',
  },
} as const;

/**
 * Toast Component
 *
 * Individual toast notification with auto-dismiss and manual close
 *
 * Features:
 * - Type-based styling (success, error, warning, info)
 * - Auto-dismiss with progress indicator
 * - Manual close button
 * - Slide-in animation
 * - Accessible (ARIA labels, keyboard support)
 *
 * @example
 * <Toast
 *   toast={{ id: '1', type: 'success', message: 'Saved!', duration: 5000 }}
 *   onClose={() => removeToast('1')}
 * />
 */
export const Toast = ({ toast, onClose }: ToastProps) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const variant = TOAST_VARIANTS[toast.type];
  const duration = toast.duration || 5000;

  useEffect(() => {
    // Auto-dismiss timer
    const dismissTimer = setTimeout(() => {
      handleClose();
    }, duration);

    // Progress bar animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const decrement = (100 / duration) * 50; // Update every 50ms
        return Math.max(0, prev - decrement);
      });
    }, 50);

    return () => {
      clearTimeout(dismissTimer);
      clearInterval(progressInterval);
    };
  }, [duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };

  return (
    <div
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      className={`
        min-w-[320px] max-w-md
        ${variant.bgColor}
        border-l-4 ${variant.borderColor}
        rounded-lg shadow-lg
        overflow-hidden
        transition-all duration-300 ease-in-out
        ${isExiting
          ? 'opacity-0 translate-x-full scale-95'
          : 'opacity-100 translate-x-0 scale-100'
        }
      `}
    >
      {/* Main content */}
      <div className="p-4 flex items-start gap-3">
        {/* Icon */}
        <div className="flex-shrink-0 pt-0.5">
          <Icon
            name={variant.icon}
            size="md"
            color={variant.iconColor}
            ariaHidden
          />
        </div>

        {/* Message */}
        <div className={`flex-1 text-sm font-medium ${variant.textColor}`}>
          {toast.message}
        </div>

        {/* Optional inline action (e.g. Undo) */}
        {toast.action && (
          <button
            type="button"
            disabled={toast.action.disabled}
            title={toast.action.title}
            onClick={() => {
              toast.action?.onClick?.();
              handleClose();
            }}
            className={`
              flex-shrink-0 px-2.5 h-7 rounded-md text-xs font-semibold
              border border-current ${variant.iconColor}
              hover:bg-black/10 dark:hover:bg-white/10
              transition-colors focus:outline-none
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent
            `}
          >
            {toast.action.label}
          </button>
        )}

        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className={`
            flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md
            ${variant.iconColor}
            hover:bg-black/10 dark:hover:bg-white/10
            transition-colors
            focus:outline-none focus:ring-2 focus:ring-offset-2
            ${variant.focusRing}
          `}
          aria-label="Close notification"
        >
          <Icon name={ICONS.CROSS} size="sm" ariaHidden />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-black/10 dark:bg-white/10">
        <div
          className={`h-full ${variant.borderColor.replace('border', 'bg')} transition-all ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};
